/**
 * CLIENTELE — Klaviyo Email Capture JS
 * Pop-up: delay + cookie suppression + keyboard close
 * Inline: form submit
 * Both: Klaviyo AJAX subscribe API
 */

(function() {
  'use strict';

  // ── Cookie helpers ──────────────────────────────────────────
  const setCookie = (name, val, days) => {
    const d = new Date();
    d.setTime(d.getTime() + days * 864e5);
    document.cookie = `${name}=${val};expires=${d.toUTCString()};path=/;SameSite=Lax`;
  };
  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null;
  };

  // ── Subscribe via Klaviyo AJAX API ─────────────────────────
  const subscribeKlaviyo = async (email, listId, formEl) => {
    const sectionId = formEl.closest('[data-section]')?.dataset.section || '';
    const successEl = document.getElementById(`clientele-klaviyo-${sectionId}-success`);
    const errorEl   = document.getElementById(`clientele-klaviyo-${sectionId}-error`);
    const errorMsg  = document.getElementById(`clientele-klaviyo-${sectionId}-error-msg`);
    const submitBtn = formEl.querySelector('.c-klav__submit');
    const submitTxt = formEl.querySelector('.c-klav__submit-text');

    // Loading state
    if (submitBtn) submitBtn.disabled = true;
    if (submitTxt) submitTxt.textContent = '...';

    try {
      let success = false;

      // Method 1: Klaviyo AJAX endpoint (requires public API key in page)
      if (listId) {
        const klaviyoPubKey = window.__klaviyo_pub_key ||
          document.querySelector('meta[name="klaviyo-public-key"]')?.content || '';

        if (klaviyoPubKey) {
          const res = await fetch(`https://manage.kmail-lists.com/ajax/subscriptions/subscribe`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({ g: listId, email, $fields: '$email', api_key: klaviyoPubKey })
          });
          const data = await res.json();
          success = data.success || data.result === 'Subscribed';
        }
      }

      // Method 2: Shopify customer newsletter (fallback)
      if (!success) {
        const formData = new FormData();
        formData.append('contact[email]', email);
        formData.append('contact[tags]', 'newsletter');
        formData.append('form_type', 'customer');
        await fetch('/contact', { method: 'POST', body: formData });
        success = true; // Assume success
      }

      if (success) {
        formEl.style.display = 'none';
        if (successEl) successEl.removeAttribute('hidden');
        // Set cookie so popup doesn't reappear
        const popup = formEl.closest('[data-cookie-name]');
        if (popup) setCookie(popup.dataset.cookieName, '1', Number(popup.dataset.cookieDays) || 30);
        // Dispatch event for analytics
        document.dispatchEvent(new CustomEvent('clientele:email-subscribe', { detail: { email, listId } }));
      }

    } catch (err) {
      if (errorMsg) errorMsg.textContent = 'Something went wrong. Try again.';
      if (errorEl) errorEl.removeAttribute('hidden');
    } finally {
      if (submitBtn) submitBtn.disabled = false;
      if (submitTxt) submitTxt.textContent = submitTxt.closest('[data-form-mode="popup"]') ? 'JOIN' : 'SUBSCRIBE';
    }
  };

  // ── Validate email ──────────────────────────────────────────
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // ── Init popup ──────────────────────────────────────────────
  const initPopup = (popup) => {
    const sectionId = popup.dataset.section;
    const delay     = Number(popup.dataset.delay) || 4000;
    const cookieName = popup.dataset.cookieName || `c_klav_${sectionId}`;
    const cookieDays = Number(popup.dataset.cookieDays) || 30;

    // Check cookie
    if (getCookie(cookieName)) return;

    const openPopup = () => {
      popup.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
      // Focus first input
      setTimeout(() => {
        const input = popup.querySelector('.c-klav__input');
        if (input) input.focus();
      }, 420);
    };

    const closePopup = () => {
      popup.setAttribute('hidden', '');
      document.body.style.overflow = '';
      setCookie(cookieName, '1', cookieDays);
    };

    // Delay trigger
    const timer = setTimeout(openPopup, delay);

    // Close button
    const closeBtn = document.getElementById(`clientele-klaviyo-${sectionId}-close`);
    if (closeBtn) closeBtn.addEventListener('click', closePopup);

    // Skip button
    const skipBtn = document.getElementById(`clientele-klaviyo-${sectionId}-skip`);
    if (skipBtn) skipBtn.addEventListener('click', closePopup);

    // Backdrop click
    const backdrop = document.getElementById(`clientele-klaviyo-${sectionId}-backdrop`);
    if (backdrop) backdrop.addEventListener('click', closePopup);

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !popup.hasAttribute('hidden')) closePopup();
    });

    // Clean up timer if user scrolls/interacts quickly
    document.addEventListener('scroll', () => clearTimeout(timer), { once: true });
  };

  // ── Init form ───────────────────────────────────────────────
  const initForm = (form) => {
    const listId = form.dataset.listId || '';
    const emailInput = form.querySelector('.c-klav__input');

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = emailInput?.value?.trim() || '';
      if (!isValidEmail(email)) {
        emailInput?.classList.add('is-invalid');
        emailInput?.focus();
        return;
      }
      emailInput?.classList.remove('is-invalid');
      await subscribeKlaviyo(email, listId, form);
    });

    // Remove invalid state on input
    emailInput?.addEventListener('input', () => emailInput.classList.remove('is-invalid'));
  };

  // ── Init all instances ──────────────────────────────────────
  const init = () => {
    // Popups
    document.querySelectorAll('.c-klav--popup').forEach(initPopup);

    // Forms
    document.querySelectorAll('.c-klav__form').forEach(initForm);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
